import React, { useEffect, useState } from 'react';
import { Layout } from "./components/dashboard/layout";
import {
  Box,
  Typography,
  Avatar,
  Alert,
  TextField,
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Divider,
  Chip,
  Stack
} from '@mui/material';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import StorageIcon from '@mui/icons-material/Storage';
import FlagCircleIcon from '@mui/icons-material/FlagCircle';
import RuleIcon from '@mui/icons-material/Rule';
import UndoIcon from '@mui/icons-material/Undo';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

// Bump this whenever the wording of the policy text changes.
// Existing consent records store the version they agreed to,
// so you can tell who needs to be asked again after an update.
const POLICY_VERSION = "1.0";

const POLICY_SECTIONS = [
  {
    icon: FaceRetouchingNaturalIcon,
    title: "What is collected",
    body: "A facial image/template is captured at enrollment and used to match against images captured at the entrance gate for identification purposes only."
  },
  {
    icon: StorageIcon,
    title: "Where it is stored",
    body: "All facial recognition data is stored exclusively on AISC's local on-campus servers. It is not uploaded to, processed by, or shared with any third-party or cloud service."
  },
  {
    icon: FlagCircleIcon,
    title: "Purpose of use",
    body: "This data is used solely to automate and verify entrance access. It is not used for any other purpose (e.g. attendance analytics, behavior monitoring, or marketing)."
  },
  {
    icon: RuleIcon,
    title: "Your choice",
    body: "Enrolling in facial recognition is optional. You may choose to use RFID card access only, with no impact on your ability to access campus."
  },
  {
    icon: UndoIcon,
    title: "Withdrawing consent",
    body: "You may withdraw this consent at any time by contacting AISC IT/Admin. Upon withdrawal, your facial recognition data will be deleted from local servers and you will be switched to card-only access."
  },
  {
    icon: EventRepeatIcon,
    title: "Retention",
    body: "Data is retained only while you are actively enrolled (e.g. as a current student, parent, or staff member) and is deleted upon withdrawal or exit from the school, per AISC's data retention policy."
  }
];

const FaceConsentCapture = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState("");

  const [existingConsent, setExistingConsent] = useState(null); // consent record from server, if any
  const [agreePolicy, setAgreePolicy] = useState(false);
  const [agreeFaceCapture, setAgreeFaceCapture] = useState(false);
  const [confirmName, setConfirmName] = useState("");

  const fetchUsers = async () => {
    try {
      const response = await fetch("https://raptornet.aischennai.org/screening-manager/gate-api/getAllUsers.php");
      const data = await response.json();
      const formattedUsers = data.users.map(user => ({
        userId: user.enroll_no,
        name: user.full_name,
        rfid: user.rfid_tag,
        role: user.user_type,
        grade_level: user.grade,
        person_pk: user.person_pk,
        image_status: user.image_status
      }));
      setUsers(formattedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchConsentStatus = async (user) => {
    setLoading("Checking consent status...");
    setExistingConsent(null);
    try {
      const response = await fetch(
        `https://raptornet.aischennai.org/screening-manager/gate-api/getFaceConsentStatus.php?person_pk=${user.person_pk}`
      );
      const result = await response.json();
      if (result.success && result.consent) {
        setExistingConsent(result.consent);
        // Pre-check boxes to reflect current state, in case they're just reviewing
        setAgreePolicy(result.consent.status === 'granted');
        setAgreeFaceCapture(result.consent.status === 'granted');
      } else {
        setExistingConsent(null);
        setAgreePolicy(false);
        setAgreeFaceCapture(false);
      }
    } catch (error) {
      console.error("Error fetching consent status:", error);
    } finally {
      setLoading("");
    }
  };

  const handleUserSelect = (event, newValue) => {
    setSelectedUser(newValue);
    setAlert(null);
    setConfirmName("");
    setAgreePolicy(false);
    setAgreeFaceCapture(false);
    if (newValue) {
      fetchConsentStatus(newValue);
    } else {
      setExistingConsent(null);
    }
  };

  const submitConsent = async (statusToSet) => {
    if (!selectedUser) return;

    if (statusToSet === 'granted') {
      if (!agreePolicy || !agreeFaceCapture) {
        setAlert({ severity: 'warning', message: 'Both consent checkboxes must be ticked before submitting.' });
        return;
      }
      if (confirmName.trim().toLowerCase() !== selectedUser.name.trim().toLowerCase()) {
        setAlert({ severity: 'warning', message: 'Typed name must match the selected user\u2019s name to confirm consent.' });
        return;
      }
    }

    setLoading(statusToSet === 'granted' ? "Recording consent..." : "Withdrawing consent...");
    setAlert(null);

    try {
      const response = await fetch('https://raptornet.aischennai.org/screening-manager/gate-api/setFaceConsentStatus.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_pk: selectedUser.person_pk,
          user_id: selectedUser.userId,
          status: statusToSet, // 'granted' | 'withdrawn'
          policy_version: POLICY_VERSION,
          confirmed_name: confirmName,
          timestamp: new Date().toISOString()
        })
      });

      const result = await response.json();
      if (result.success) {
        setAlert({
          severity: 'success',
          message: statusToSet === 'granted'
            ? 'Face recognition consent recorded successfully.'
            : 'Face recognition consent withdrawn. User will be switched to card-only access.'
        });
        fetchConsentStatus(selectedUser);
      } else {
        setAlert({ severity: 'error', message: result.message || 'Failed to update consent status.' });
      }
    } catch (error) {
      console.error('Consent API error:', error);
      setAlert({ severity: 'error', message: 'Error contacting consent API.' });
    } finally {
      setLoading("");
    }
  };

  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => setAlert(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <Layout>
      <Box sx={{ p: 4, maxWidth: 900, mx: 'auto' }}>
        <Typography variant="h4" gutterBottom>Face Recognition Consent</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Collect and manage consent for facial recognition entrance access. All biometric data is stored on AISC's local on-campus servers only.
        </Typography>

        {alert && <Alert severity={alert.severity} sx={{ mt: 2, mb: 2 }}>{alert.message}</Alert>}

        <Autocomplete
          sx={{ mt: 3 }}
          options={users}
          getOptionLabel={(option) => `${option.userId} - ${option.name}`}
          onChange={handleUserSelect}
          renderOption={(props, option) => (
            <li {...props} data-rfid={option.rfid}>{`${option.userId} - ${option.name}`}</li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Search User" variant="outlined" fullWidth />
          )}
        />

        {selectedUser && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, p: 3, borderRadius: '10px', backgroundColor: '#f5f5f5' }}>
              <Avatar
                src={`https://raptornet.aischennai.org/screening-manager/api/images/${selectedUser.userId}.jpg`}
                alt={selectedUser.name}
                sx={{ width: 100, height: 100 }}
              />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5">{selectedUser.name}</Typography>
                <Typography variant="body1" color="text.secondary">{selectedUser.userId}</Typography>
              </Box>
              {existingConsent && (
                <Chip
                  label={existingConsent.status === 'granted' ? 'Consent: Granted' : 'Consent: Withdrawn'}
                  color={existingConsent.status === 'granted' ? 'success' : 'default'}
                  variant="outlined"
                />
              )}
            </Box>

            {existingConsent && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Last updated: {existingConsent.updated_at || 'N/A'} · Policy version agreed: {existingConsent.policy_version || 'N/A'}
              </Typography>
            )}

            <Paper
              variant="outlined"
              sx={{
                mt: 3,
                borderRadius: 2,
                overflow: 'hidden',
                borderColor: 'divider'
              }}
            >
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#eef3f8',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <VerifiedUserIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Face Recognition & Entrance Access — Consent Notice
                  </Typography>
                </Stack>
                <Chip size="small" label={`Policy v${POLICY_VERSION}`} variant="outlined" />
              </Box>

              <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                {POLICY_SECTIONS.map((section, idx) => {
                  const Icon = section.icon;
                  return (
                    <Box key={section.title}>
                      <Box sx={{ display: 'flex', gap: 2, px: 3, py: 2 }}>
                        <Icon sx={{ color: 'text.secondary', mt: '2px' }} fontSize="small" />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {section.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {section.body}
                          </Typography>
                        </Box>
                      </Box>
                      {idx < POLICY_SECTIONS.length - 1 && <Divider />}
                    </Box>
                  );
                })}
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ mt: 2, p: 2.5, borderRadius: 2, backgroundColor: '#fafafa' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreePolicy}
                    onChange={(e) => setAgreePolicy(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    I have read and understood the notice above, including that data is stored on local servers only.
                  </Typography>
                }
              />
              <FormControlLabel
                sx={{ display: 'flex', mt: 0.5 }}
                control={
                  <Checkbox
                    checked={agreeFaceCapture}
                    onChange={(e) => setAgreeFaceCapture(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    I consent to the collection and use of my facial recognition data for entrance access automation.
                  </Typography>
                }
              />
            </Paper>

            <TextField
              sx={{ mt: 2 }} 
              label="Type full name to confirm"
              placeholder={selectedUser.name}
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              fullWidth
            />

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => submitConsent('granted')}
                disabled={loading !== ""}
              >
                Grant Consent
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => submitConsent('withdrawn')}
                disabled={loading !== "" || !existingConsent || existingConsent.status !== 'granted'}
              >
                Withdraw Consent
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default FaceConsentCapture;